<template>
  <v-card class="pa-4 fs-card" elevation="1" fs-card>
    <v-card-title>
      <div class="fs-title2">Finding Overview</div>

      <div class="fs-actions">
        <template v-if="!isEditing">
          <v-btn size="small" variant="tonal" color="primary" class="fs-edit" @click="beginEdit">
            <v-icon start size="18">mdi-pencil-outline</v-icon>
            Edit
          </v-btn>
        </template>

        <template v-else>
          <v-btn
            size="small"
            variant="text"
            color="grey"
            class="fs-edit"
            @click="cancel"
            style="margin-left: 10px"
          >
            <v-icon start size="18">mdi-close</v-icon>
            Cancel
          </v-btn>

          <!-- Hidden when the parent supplies a single external Save button
               (e.g. the Finding page header saves the fields + form together). -->
          <v-btn
            v-if="!externalSave"
            size="small"
            variant="flat"
            color="green"
            class="fs-edit"
            @click="save"
            style="right: 160px"
          >
            <v-icon start size="18">mdi-content-save</v-icon>
            Save
          </v-btn>
        </template>

        <v-btn
          v-if="closable"
          class="fs-close"
          icon
          variant="text"
          :aria-label="'Close'"
          @click="$emit('close')"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>
    </v-card-title>

    <v-row>
      <!-- LEFT -->
      <v-col cols="12" md="6">
        <div class="fs-head w-100">
          <div class="fs-left">
            <div class="fs-title-row">
              <div class="fs-title-wrap">
                <!-- Chips / Selects -->
                <div>
                  <!-- Priority -->
                  <template v-if="!isEditing">
                    <v-chip
                      size="x-small"
                      variant="elevated"
                      :color="priorityColorChip(finding)"
                      class="fs-chip"
                    >
                      <v-icon start size="14">mdi-circle-outline</v-icon>
                      Priority: {{ priorityLabel(finding) }}
                    </v-chip>
                  </template>
                  <template v-else>
                    <v-select
                      v-model="draft.priorityId"
                      :items="priorities"
                      item-title="priority"
                      :item-value="(p) => Number(p.priorityId)"
                      label="Priority"
                      density="comfortable"
                      hide-details
                      class="mr-2 mb-2 fs-meta-select"
                    />
                  </template>
                  <audit-field-timeline
                    v-if="finding && finding.findingId"
                    entity-type="Finding"
                    :record-id="finding.findingId"
                    field-key="PriorityFK"
                    field-label="Priority"
                  />

                  <v-chip
                    size="x-small"
                    variant="tonal"
                    :color="photoReviewColor(finding.photoReviewStatus)"
                    class="fs-chip ml-2"
                    prepend-icon="mdi-image-check-outline"
                  >
                    Photos: {{ finding.photoReviewStatus || 'No photos' }}<span v-if="finding.photoCount"> ({{ finding.photoCount }})</span>
                  </v-chip>

                  <!-- Status -->
                  <template v-if="!isEditing">
                    <v-chip
                      size="x-small"
                      variant="elevated"
                      :color="statusColorChip(finding)"
                      text-color="white"
                      class="fs-chip"
                      style="margin-left: 8px"
                    >
                      <v-icon start size="14">mdi-circle-outline</v-icon>
                      Status: {{ finding.statusFK }}
                    </v-chip>
                  </template>
                  <template v-else>
                    <v-select
                      v-model="draft.statusId"
                      :items="statuses"
                      item-title="findingStatus"
                      :item-value="(s) => Number(s.findingStatusId)"
                      label="Status"
                      density="comfortable"
                      hide-details
                      class="mb-2 fs-meta-select"
                    />
                  </template>
                  <audit-field-timeline
                    v-if="finding && finding.findingId"
                    entity-type="Finding"
                    :record-id="finding.findingId"
                    field-key="StatusFK"
                    field-label="Status"
                  />

                  <!-- Zone name (readonly) -->
                  <div class="text-caption text-medium-emphasis mt-1 d-flex align-center flex-wrap">
                    <v-icon size="16" class="mr-1">mdi-map-marker</v-icon>
                    {{ finding.zoneNameFK }}
                    <audit-field-timeline
                      v-if="finding && finding.findingId"
                      entity-type="Finding"
                      :record-id="finding.findingId"
                      field-key="ZoneNameFK"
                      field-label="Zone"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Submitted By -->
        <div class="fs-section-heading mb-2">Submitted by</div>
        <div class="d-flex align-center mb-4 fs-info-row">
          <v-avatar size="32" class="mr-2 fs-user-avatar">
            <img :src="getUserIconUrl(finding.submittedBy)" />
          </v-avatar>
          <span class="text-body-2 fs-info-value fs-user-name"><strong>{{ finding.submittedByFK || finding.submittedBy || "Unknown user" }}</strong></span>
        </div>

        <div class="fs-section-heading mb-2">Safety and dates</div>
        <v-checkbox v-if="isEditing" v-model="draft.isSafetyIssue" label="Safety issue"
          :indeterminate="draft.isSafetyIssue == null" color="error" hide-details />
        <v-checkbox v-else :model-value="finding.isSafetyIssue === true" label="Safety issue"
          :indeterminate="finding.isSafetyIssue == null" color="error" disabled hide-details />
        <div v-if="(isEditing ? draft.isSafetyIssue : finding.isSafetyIssue) == null" class="text-caption mb-2">Safety assessment not recorded</div>
        <div class="mb-3 fs-meta-card">
          <div class="fs-section-heading mb-1">Date raised</div>
          <div v-if="!isEditing" class="text-body-2 fs-info-value d-flex align-center">
            <v-icon size="16" class="mr-1">mdi-calendar-clock</v-icon>
            {{ submitDateDisplay || 'Not recorded' }}
          </div>
          <v-text-field
            v-else
            v-model="draft.submitTime"
            label="Date raised"
            type="datetime-local"
            density="comfortable"
            hide-details
          />
        </div>

        <div v-for="field in [{ key: 'dateMadeSafe', label: 'Date made safe' }, { key: 'dateResolved', label: 'Date resolved' }]"
          :key="field.key" class="mb-3 fs-meta-card">
          <div class="fs-section-heading mb-1">{{ field.label }}</div>
          <v-text-field v-if="isEditing" v-model="draft[field.key]" type="datetime-local"
            :label="field.label" density="comfortable" clearable hide-details />
          <div v-else class="text-body-2 fs-info-value">{{ displaySafetyDate(finding[field.key]) }}</div>
        </div>

        <!-- Inspection -->
        <div class="mb-3 fs-meta-card">
          <div class="fs-section-heading mb-1 d-flex align-center">
            Inspection
            <audit-field-timeline
              v-if="finding && finding.findingId"
              entity-type="Finding"
              :record-id="finding.findingId"
              field-key="InspectionNameFK"
              field-label="Inspection"
            />
          </div>
          <div v-if="!isEditing" class="text-body-2 fs-info-value">{{ finding.inspectionNameFK }}</div>
          <v-select
            v-else
            v-model="draft.inspectionId"
            :items="inspections"
            item-title="name"
            item-value="inspectionId"
            density="comfortable"
            hide-details
            clearable
          />
        </div>

        <!-- Description -->
        <div class="mb-4 fs-meta-card">
          <div class="fs-section-heading mb-1 d-flex align-center">
            Description
            <audit-field-timeline
              v-if="finding && finding.findingId"
              entity-type="Finding"
              :record-id="finding.findingId"
              field-key="Details"
              field-label="Description"
            />
          </div>

          <div v-if="!isEditing" class="text-body-2 fs-description">
            {{ finding.details }}
          </div>
          <v-textarea
            v-else
            v-model="draft.details"
            label="Description"
            auto-grow
            density="comfortable"
            hide-details
          />
        </div>

        <!-- Media -->
        <div>
          <div class="fs-section-heading mb-2 d-flex align-center justify-space-between">
            <span>Media</span>
            <div class="d-flex align-center" style="gap: 8px">
              <v-btn
                color="primary"
                variant="tonal"
                size="small"
                prepend-icon="mdi-camera-plus"
                :disabled="uploadingPhoto || !finding?.findingId"
                @click="$refs.photoFileInput.click()"
              >
                Add Photos
              </v-btn>
              <input
                ref="photoFileInput"
                type="file"
                accept="image/*"
                multiple
                style="display: none"
                @change="onPhotoInputChange"
              />
              <v-btn
                v-if="photoSnapshots.length > 0"
                color="primary"
                size="small"
                variant="flat"
                :disabled="uploadingPhoto || !finding?.findingId"
                :loading="uploadingPhoto"
                @click="uploadSelectedPhotos"
              >
                <v-icon start size="18">mdi-upload</v-icon>
                Upload ({{ photoSnapshots.length }})
              </v-btn>
            </div>
          </div>
          <!-- Queued (not yet uploaded) — small thumbnails with × to drop -->
          <v-row v-if="queuedPhotoPreviews.length" dense class="mb-2">
            <v-col
              v-for="(preview, idx) in queuedPhotoPreviews"
              :key="preview.url"
              cols="auto"
            >
              <v-sheet
                elevation="1"
                rounded
                class="pa-0 position-relative"
                style="overflow: hidden; width: 80px; height: 80px; border: 2px dashed rgba(25, 118, 210, 0.5)"
              >
                <v-img
                  :src="preview.url"
                  height="80"
                  width="80"
                  class="rounded"
                  cover
                />
                <v-btn
                  icon
                  size="x-small"
                  color="error"
                  variant="flat"
                  class="position-absolute"
                  style="top: 2px; right: 2px; z-index: 10"
                  :disabled="uploadingPhoto"
                  @click.stop="removeQueuedPhoto(idx)"
                >
                  <v-icon size="16">mdi-close</v-icon>
                </v-btn>
              </v-sheet>
              <div class="text-caption text-grey text-truncate mt-1" style="max-width: 80px" :title="preview.name">
                {{ formatFileSize(preview.size) }}
              </div>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col
              v-for="photo in effectiveMedia"
              :key="photo.photoUrlId || photo.PhotoUrlId || photo.url"
              cols="6"
              sm="4"
            >
              <v-sheet
                elevation="1"
                rounded
                width="100%"
                class="pa-0 position-relative"
                style="overflow: hidden; cursor: pointer"
              >
                <v-img
                  :src="photo.url || photo.Url"
                  height="110"
                  class="rounded"
                  cover
                  @click="openLightbox(photo)"
                />
                <v-btn
                  icon
                  size="x-small"
                  color="error"
                  variant="flat"
                  class="position-absolute"
                  style="top: 2px; right: 2px; z-index: 10"
                  @click.stop="deletePhoto(photo)"
                >
                  <v-icon size="16">mdi-delete</v-icon>
                </v-btn>
              </v-sheet>
              <div
                v-if="formatTaken(photo)"
                class="text-caption text-grey text-center mt-1"
                style="line-height: 1.1"
                :title="formatTakenFull(photo)"
              >
                {{ formatTaken(photo) }}
              </div>
            </v-col>

            <v-col v-if="!effectiveMedia.length" cols="12" class="text-center text-grey">
              <span>No images found for this finding. Use the upload button above to add images.</span>
            </v-col>
          </v-row>

          <v-dialog
            v-model="lightbox.show"
            fullscreen
            content-class="lightbox-dialog"
            @click:outside="lightbox.show = false"
          >
            <div class="lightbox-overlay" @click.self="lightbox.show = false">
              <img :src="lightbox.img" class="lightbox-image" />
              <v-btn
                icon
                variant="elevated"
                color="white"
                size="default"
                class="lightbox-close"
                @click="lightbox.show = false"
              >
                <v-icon size="24" color="#333">mdi-close</v-icon>
              </v-btn>
              <v-btn
                color="primary"
                prepend-icon="mdi-image-search"
                class="lightbox-analyse"
                @click.stop="analysePhoto"
              >
                Analyse with AI
              </v-btn>
            </div>
          </v-dialog>
        </div>

        <!-- Tasks linked to this finding -->
        <div v-if="finding?.findingId" class="mb-3">
          <v-tooltip :text="`Tasks (${findingTasksCount})`" location="top">
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                icon
                size="x-small"
                variant="text"
                color="primary"
                @click="goToTasks"
              >
                <v-icon size="14">mdi-clipboard-list-outline</v-icon>
              </v-btn>
            </template>
          </v-tooltip>
        </div>

        <!-- Location (pretty) -->
        <div class="fs-section-heading mb-2">Location</div>
        <div class="">
          <CoordinateDisplay :lat="currentLat" :lon="currentLon" layout="inline" />
        </div>
      </v-col>

      <!-- RIGHT: Map -->
      <v-col v-if="showMap" cols="12" md="6" class="d-flex align-center justify-center">
        <!-- read-only -->
        <zone-map-simple
          v-if="!isEditing"
          :center="[finding.latitude, finding.longitude]"
          :zoom="mapZoom"
          :marker="[finding.latitude, finding.longitude]"
          :marker-data="findingMarkerData"
        />
        <!-- editable map -->
        <div
          v-else
          ref="editMap"
          style="height: 320px; width: 100%; border-radius: 8px; overflow: hidden"
        ></div>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import ZoneMapSimple from "@/components/ZoneMapSimple.vue";
import { findingDate, findingDateInput, findingDatePayload } from "@/utils/findingDates";
import CoordinateDisplay from "@/components/CoordinateDisplay.vue";
import AuditFieldTimeline from "@/components/audit/AuditFieldTimeline.vue";
import { getPhotosByFindingId, deletePhotoItem, uploadPhoto, snapshotPhotoFiles, revokePhotoSnapshot, revokePhotoSnapshots } from "@/api/photoItems";
import { getTasksByFindingId } from "@/api/tasks";
import { useDialogStore } from "@/stores/dialog";
import { useToast } from "vue-toastification";
import L from "leaflet";
import { markRaw } from "vue";
// See WayMap.vue: wrap every Leaflet object assigned to `this.*` in markRaw()
// so Vue's reactive Proxy doesn't break Leaflet cleanup and crash on zoom.
import { createMarker } from "@/utils/markerFactory";

export default {
  name: "FindingSummaryCard",
  components: { ZoneMapSimple, CoordinateDisplay, AuditFieldTimeline },
  props: {
    finding: { type: Object, required: true },
    // The form template for this finding (optional). When provided we use its
    // iconName/iconColour as a fallback for older findings whose own IconName
    // column is NULL — without this they render as the orange exclamation.
    form: { type: Object, default: null },

    /** Arrays from API */
    // NEW: lookups
    statuses: {
      type: Array,
      default: () => [
        { FindingStatusId: 1, FindingStatus: "Action Needed", Colour: "red", CustomerId: 0 },
        { FindingStatusId: 2, FindingStatus: "Task Assigned", Colour: "amber", CustomerId: 0 },
        { FindingStatusId: 3, FindingStatus: "Resolved", Colour: "green", CustomerId: 0 },
      ],
    },
    priorities: {
      type: Array,
      default: () => [
        { PriorityId: 1, Priority: "Low", Colour: "#2AA876" },
        { PriorityId: 2, Priority: "Medium", Colour: "#E9C46A" },
        { PriorityId: 3, Priority: "High", Colour: "#E76F51" },
      ],
    },
    media: { type: Array, default: () => [] },
    // Inspections available for the editable Inspection dropdown (edit mode).
    inspections: { type: Array, default: () => [] },
    // When true, the panel hides its own Save button — the parent drives saving
    // (via buildDraftDto) so the finding fields and the form save as one action.
    externalSave: { type: Boolean, default: false },
    autoFetchMedia: { type: Boolean, default: true },
    title: { type: String, default: "Finding Summary" },

    showMap: { type: Boolean, default: true },
    mapZoom: { type: Number, default: 12 },
    closable: { type: Boolean, default: true },
  },
  emits: ["save", "close", "media-updated"],
  data() {
    return {
      internalMedia: [],
      lightbox: { show: false, img: null, photo: null },
      isEditing: false,
      draft: null,
      findingTasksCount: 0,
      photoSnapshots: [],
      deletingPhoto: false,
      uploadingPhoto: false,

      map: null,
      marker: null,
    };
  },
  setup() {
    const dialog = useDialogStore();
    const toast = useToast();
    return { dialog, toast };
  },
  computed: {
    queuedPhotoPreviews() {
      // Read the stable preview URL minted once in onPhotoInputChange (the
      // snapshot's previewUrl). We do NOT call URL.createObjectURL() here —
      // a computed runs on every render and would mint (and leak) a fresh
      // blob URL each tick, which also breaks thumbnails on iOS WebKit.
      return (this.photoSnapshots || []).map(s => ({ url: s.previewUrl, name: s.name, size: s.size }));
    },
    effectiveMedia() {
      // Prefer internalMedia if it has items (freshly fetched), otherwise use media prop
      // This ensures uploaded images show up immediately
      const result = (this.internalMedia && this.internalMedia.length > 0)
        ? this.internalMedia
        : (this.media || this.internalMedia || []);

      // Debug logging
      if (result.length > 0) {
        console.log(`[EFFECTIVE MEDIA] Returning ${result.length} items. First item:`, result[0]);
        console.log(`[EFFECTIVE MEDIA] First item URL:`, result[0]?.url || result[0]?.Url);
      } else {
        console.log(`[EFFECTIVE MEDIA] Empty - internalMedia: ${this.internalMedia?.length || 0}, media prop: ${this.media?.length || 0}`);
      }

      return result;
    },
    currentLat() {
      return this.isEditing ? this.draft?.latitude : this.finding?.latitude;
    },
    currentLon() {
      return this.isEditing ? this.draft?.longitude : this.finding?.longitude;
    },
    findingMarkerData() {
      // Resolve icon in priority order: finding's own iconName (the cheap win
      // for findings created with a form-icon already baked in) → finding's
      // PascalCase variant (legacy server payload) → form template iconName
      // (passed down from Finding.vue when loaded). Without this last fallback,
      // older findings whose IconName column is NULL render with the orange
      // exclamation default from markerFactory.
      const fromFinding =
        this.firstNonEmpty(this.finding?.iconName, this.finding?.IconName);
      const fromForm =
        this.firstNonEmpty(this.form?.iconName, this.form?.IconName);
      const colourFromFinding =
        this.firstNonEmpty(this.finding?.iconColour, this.finding?.IconColour);
      const colourFromForm =
        this.firstNonEmpty(this.form?.iconColour, this.form?.IconColour);

      const resolved = {
        title: this.finding?.title,
        iconName: fromFinding || fromForm || "",
        iconColour: colourFromFinding || colourFromForm || "",
      };

      // Debug — paste console output if marker still shows the orange fallback.
      console.log("[FINDING ICON]", {
        findingId: this.finding?.findingId,
        formId: this.finding?.formId,
        finding_iconName: this.finding?.iconName,
        finding_IconName: this.finding?.IconName,
        form_iconName: this.form?.iconName,
        form_IconName: this.form?.IconName,
        resolved_iconName: resolved.iconName || "(empty → orange fallback)",
      });

      return resolved;
    },
    // Human-readable submit date for the overview. SubmitTime is set server-side
    // when the finding is created (FindingRepository), so it's the "logged on" date.
    submitDateDisplay() {
      const d = this.firstNonEmpty(this.finding?.submitTime, this.finding?.SubmitTime);
      if (!d) return "";
      try {
        return findingDate(d)?.toLocaleString("en-GB", {
          day: "2-digit", month: "short", year: "numeric",
          hour: "2-digit", minute: "2-digit",
        });
      } catch {
        return String(d);
      }
    },
    // Parse the finding's dynamic-form answers (stored as a JSON array of fields
    // in finding.data) into a flat list of label/value pairs for read-only display.
    // Display-only field types (headers, dividers, etc.) and empty fields are skipped.
    formDetails() {
      const raw = this.finding?.data ?? this.finding?.Data;
      if (!raw || typeof raw !== "string" || !raw.trim()) return [];

      let parsed;
      try {
        parsed = JSON.parse(raw);
      } catch {
        return [];
      }
      if (!Array.isArray(parsed)) return [];

      const skipTypes = new Set([
        "header", "heading", "section", "paragraph", "label", "divider", "info", "button",
      ]);

      const rows = [];
      for (const field of parsed) {
        if (!field || typeof field !== "object") continue;
        const type = String(field.type ?? field.Type ?? "").toLowerCase();
        if (skipTypes.has(type)) continue;

        const label = this.firstNonEmpty(
          field.label, field.Label, field.name, field.Name
        );
        const { text, image } = this.formatFieldValue(field.value ?? field.Value, type);

        // Skip fields that carry neither a label nor a captured value.
        if (!label && !text && !image) continue;
        rows.push({ label: label || "(field)", value: text, image });
      }
      return rows;
    },
  },
  watch: {
    finding: {
      immediate: true,
      async handler(f) {
        // fetch media if parent didn't pass
        if (this.autoFetchMedia && f?.findingId) {
          try {
            const freshMedia = await getPhotosByFindingId(f.findingId);
            // Normalize the response to lowercase keys
            const normalizedMedia = (freshMedia || []).map(photo => ({
              photoUrlId: photo.PhotoUrlId || photo.photoUrlId,
              url: photo.Url || photo.url,
              customerId: photo.CustomerId || photo.customerId,
              zoneId: photo.ZoneId || photo.zoneId,
              findingId: photo.FindingId || photo.findingId,
              localUrl: photo.LocalUrl || photo.localUrl,
              synced: photo.Synced || photo.synced,
              createdAt: photo.CreatedAt || photo.createdAt,
              latitude: photo.Latitude || photo.latitude,
              longitude: photo.Longitude || photo.longitude,
              formItemId: photo.FormItemId || photo.formItemId,
              type: photo.Type || photo.type
            }));
            this.internalMedia = normalizedMedia;
          } catch (e) {
            this.internalMedia = [];
          }
        }
        // if finding changes while not editing, reset draft
        if (!this.isEditing) {
          this.draft = null;
        }
        this.loadFindingTasksCount();
      },
    },
    isEditing: {
      immediate: true,
      async handler(val) {
        if (val) {
          await this.$nextTick(); // wait until <div ref="editMap"> exists & has layout
          this.initEditMap();
        } else {
          this.destroyMap();
        }
      },
    },
  },
  methods: {
    firstNonEmpty(...values) {
      for (const value of values) {
        if (typeof value === "string" && value.trim()) return value.trim();
      }
      return "";
    },
    // Coerce a form field's answer into something displayable.
    // Returns { text, image } — image is set only for base64/data-URL captures
    // (e.g. signatures) so the template can render an <img> instead of text.
    formatFieldValue(val, /* type */) {
      if (val == null || val === "") return { text: "—", image: null };

      if (typeof val === "string" && /^data:image\//i.test(val)) {
        return { text: "", image: val };
      }
      if (Array.isArray(val)) {
        const parts = val
          .map((v) => {
            if (v == null) return "";
            if (typeof v === "object") {
              return v.label ?? v.Label ?? v.value ?? v.Value ?? "";
            }
            return String(v);
          })
          .filter((s) => String(s).trim());
        return { text: parts.length ? parts.join(", ") : "—", image: null };
      }
      if (typeof val === "boolean") return { text: val ? "Yes" : "No", image: null };
      if (typeof val === "object") {
        const inner = val.label ?? val.Label ?? val.value ?? val.Value;
        return { text: inner != null ? String(inner) : JSON.stringify(val), image: null };
      }
      return { text: String(val), image: null };
    },
    priorityLabel(row) {
      const id = row.priority;
      const hit = this.priorities.find((p) => String(p.priorityId) === String(id));
      return hit?.priority ?? "—";
    },
    photoReviewColor(status) {
      return ({ "Reviewed": "success", "Partially reviewed": "info", "Review required": "warning", "Analysis pending": "blue", "Not analysed": "grey", "No photos": "grey-lighten-1" })[status] || "grey";
    },
    // ---------- UI helpers ----------
    openLightbox(photo) {
      this.lightbox.photo = photo;
      this.lightbox.img = photo?.url || photo?.Url;
      this.lightbox.show = true;
    },
    analysePhoto() {
      const photo = this.lightbox.photo;
      const imageId = photo?.photoUrlId || photo?.PhotoUrlId;
      const contextId = this.finding?.findingId || this.finding?.FindingId;
      if (!imageId || !contextId) {
        this.toast.error("This photo must be synced before it can be analysed.");
        return;
      }
      this.lightbox.show = false;
      this.$router.push({ path: "/reports/ai-images", query: {
        new: "1", imageId, contextType: "Finding", contextId,
        zoneId: this.finding?.zoneId || this.finding?.ZoneId || photo?.zoneId || photo?.ZoneId || undefined,
      }});
    },
    // When the photo was taken. Short (dd/mm/yyyy) for the caption; full
    // (with time) for the hover title. Handles both camel/Pascal casing.
    formatTaken(photo) {
      const raw = photo?.createdAt ?? photo?.CreatedAt;
      if (!raw) return "";
      const d = new Date(raw);
      return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-GB");
    },
    formatTakenFull(photo) {
      const raw = photo?.createdAt ?? photo?.CreatedAt;
      if (!raw) return "";
      const d = new Date(raw);
      return Number.isNaN(d.getTime()) ? "" : d.toLocaleString("en-GB");
    },
    getUserIconUrl(id) {
      const key = String(Math.min(Math.max(parseInt(id, 10) || 1, 1), 50)).padStart(2, "0");
      return `/images/avatars/${key}.svg`;
    },
    async loadFindingTasksCount() {
      const id = this.finding?.findingId ?? this.finding?.FindingId;
      if (!id) {
        this.findingTasksCount = 0;
        return;
      }
      try {
        const tasks = await getTasksByFindingId(id);
        this.findingTasksCount = Array.isArray(tasks) ? tasks.length : 0;
      } catch {
        this.findingTasksCount = 0;
      }
    },
    goToTasks() {
      const id = this.finding?.findingId ?? this.finding?.FindingId;
      if (id) this.$router.push({ path: "/tasks", query: { findingId: id } });
    },

    // ---------- Edit flow ----------

    cancel() {
      this.isEditing = false;
      this.draft = null;
    },
    statusById(id) {
      return this.statuses.find((s) => String(s.FindingStatusId) === String(id));
    },
    priorityById(id) {
      return this.priorities.find((p) => String(p.PriorityId) === String(id));
    },

    statusLabelFromId(id) {
      return this.statusById(id)?.FindingStatus ?? "—";
    },
    priorityLabelFromId(id) {
      return this.priorityById(id)?.Priority ?? "—";
    },

    priorityColorChip(row) {
      // prefer id → name → stored colour
      const byId = this.priorityById(row.priority ?? row.priorityId);
      if (byId?.Colour) return byId.Colour;

      const byName = this.priorities.find(
        (p) => (p.Priority || "").toLowerCase() === (row.priorityFK || "").toLowerCase()
      );
      if (byName?.Colour) return byName.Colour;

      return row.priorityColour || "#9e9e9e";
    },

    statusColorChip(row) {
      const byId = this.statusById(row.statusId);
      if (byId?.Colour) return String(byId.Colour).toLowerCase();

      const byName = this.statuses.find(
        (s) => (s.FindingStatus || "").toLowerCase() === (row.statusFK || "").toLowerCase()
      );
      if (byName?.Colour) return String(byName.Colour).toLowerCase();

      return (row.statusColour || "grey").toLowerCase();
    },
    numOrNull(v) {
      return v == null || v === "" ? null : Number(v);
    },
    // Convert an ISO/Date value into the "YYYY-MM-DDTHH:mm" string that a
    // <input type="datetime-local"> expects (local time, no seconds/zone).
    toDatetimeLocal(value) {
      return findingDateInput(value);
    },
    displaySafetyDate(value) {
      return findingDate(value)?.toLocaleString() || 'Not recorded';
    },

    // Resolve the status id to preselect in the edit dropdown. Some legacy
    // findings store a status id from a different customer's scheme (e.g. 1)
    // that isn't in THIS customer's status list, while the read query still
    // resolves a name (statusFK) via an unscoped join. In that case match by
    // name so edit reflects what view shows and saving rewrites the correct id.
    resolveStatusId() {
      const raw = this.numOrNull(this.finding.statusId);
      const idOf = (s) => Number(s.findingStatusId ?? s.FindingStatusId);
      const list = this.statuses || [];
      if (raw != null && list.some((s) => idOf(s) === raw)) return raw;
      const name = (this.finding.statusFK ?? "").toLowerCase().trim();
      if (name) {
        const byName = list.find(
          (s) => String(s.findingStatus ?? s.FindingStatus ?? "").toLowerCase().trim() === name
        );
        if (byName) return idOf(byName);
      }
      return raw;
    },
    beginEdit() {
      this.draft = {
        findingId: this.finding.findingId,
        title: this.finding.title ?? "",
        details: this.finding.details ?? "",
        statusId: this.resolveStatusId(), // 👈 number (matched by name if the raw id isn't in this customer's list)
        priorityId: this.numOrNull(this.finding.priority ?? this.finding.priorityId), // 👈 number
        latitude: this.finding.latitude ?? null,
        longitude: this.finding.longitude ?? null,
        zoneId: this.finding.zoneId ?? null,
        inspectionId: this.finding.inspectionId ?? null,
        submitTime: this.toDatetimeLocal(this.finding.submitTime),
        isSafetyIssue: this.finding.isSafetyIssue ?? null,
        dateMadeSafe: this.toDatetimeLocal(this.finding.dateMadeSafe),
        dateResolved: this.toDatetimeLocal(this.finding.dateResolved),
      };
      this.isEditing = true;
    },
    ensureDraft() {
      if (!this.isEditing || !this.draft) {
        this.beginEdit();
      }
    },
    onQuickMetaChange(field, value) {
      this.ensureDraft();
      if (!this.draft) return;
      this.draft[field] = this.numOrNull(value);
    },
    // Build the FindingUpdateDto from the current draft. Returns null when not
    // editing. Exposed so a parent with an external Save button can persist the
    // finding fields alongside the form in one action.
    buildDraftDto() {
      if (!this.draft) return null;
      return {
        // === FindingUpdateDto shape ===
        FindingId: this.finding.findingId, // required
        Title: (this.draft.title ?? this.finding.title ?? "").trim(), // required
        Details: this.draft.details ?? this.finding.details ?? null,
        StatusId: this.draft.statusId != null ? Number(this.draft.statusId) : null,
        Priority:
          this.draft.priorityId != null
            ? Number(this.draft.priorityId)
            : Number(this.finding.priority ?? 0),
        Latitude: this.draft.latitude != null ? Number(this.draft.latitude) : null,
        Longitude: this.draft.longitude != null ? Number(this.draft.longitude) : null,
        InspectionId: this.draft.inspectionId || null,
        SubmitTime: findingDatePayload(this.draft.submitTime, this.finding.submitTime) ?? this.finding.submitTime ?? null,
        IsSafetyIssue: this.draft.isSafetyIssue,
        DateMadeSafe: findingDatePayload(this.draft.dateMadeSafe, this.finding.dateMadeSafe),
        DateResolved: findingDatePayload(this.draft.dateResolved, this.finding.dateResolved),
      };
    },
    save() {
      const dto = this.buildDraftDto();
      this.$emit("save", dto); // parent will call updateFinding(dto.FindingId, dto)
      this.isEditing = false;
      this.draft = null;
    },

    // ---------- Map edit ----------
    async initEditMap() {
      const el = this.$refs.editMap;
      if (!el) return;

      // clean up if somehow already there
      if (this.map) {
        this.destroyMap();
      }

      const lat = Number(this.currentLat) || 53.5;
      const lon = Number(this.currentLon) || -8.0;

      this.map = markRaw(L.map(el).setView([lat, lon], this.mapZoom || 13));
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "&copy; OpenStreetMap contributors",
      }).addTo(this.map);

      const { marker } = createMarker(
        {
          type: "finding",
          lat,
          lng: lon,
          ...this.findingMarkerData,
        },
        {},
        { markerOptions: { draggable: true } }
      );
      this.marker = markRaw(marker.addTo(this.map));

      this.map.on("click", (e) => {
        this.marker.setLatLng(e.latlng);
        this.applyMarker(e.latlng);
      });
      this.marker.on("dragend", () => {
        const ll = this.marker.getLatLng();
        this.applyMarker(ll);
      });

      // ensure proper sizing after it paints
      setTimeout(() => this.map?.invalidateSize(), 0);
    },
    applyMarker(ll) {
      if (!this.draft) return;
      this.draft.latitude = Number(ll.lat.toFixed(6));
      this.draft.longitude = Number(ll.lng.toFixed(6));
    },
    destroyMap() {
      if (this.map) {
        this.map.off();
        this.map.remove();
        this.map = null;
        this.marker = null;
      }
    },

    // ---------- Photo Management ----------
    async deletePhoto(photo) {
      const photoUrlId = photo.photoUrlId || photo.PhotoUrlId;
      if (!photoUrlId) {
        console.error("Photo object:", photo);
        this.toast.error("Cannot delete photo: missing ID");
        return;
      }

      const result = await this.dialog.confirm({
        title: "Delete Image",
        message: "Are you sure you want to delete this image?",
        variant: "warn",
        buttons: "ok-cancel"
      });

      if (result !== "ok") return;

      this.deletingPhoto = true;
      try {
        console.log("Deleting photo with ID:", photoUrlId);
        await deletePhotoItem(photoUrlId);
        this.toast.success("Image deleted successfully");

        // Refresh media list from API
        if (this.finding?.findingId) {
          const freshMedia = await getPhotosByFindingId(this.finding.findingId);
          // Normalize the response to lowercase keys
          const normalizedMedia = (freshMedia || []).map(photo => ({
            photoUrlId: photo.PhotoUrlId || photo.photoUrlId,
            url: photo.Url || photo.url,
            customerId: photo.CustomerId || photo.customerId,
            zoneId: photo.ZoneId || photo.zoneId,
            findingId: photo.FindingId || photo.findingId,
            localUrl: photo.LocalUrl || photo.localUrl,
            synced: photo.Synced || photo.synced,
            createdAt: photo.CreatedAt || photo.createdAt,
            latitude: photo.Latitude || photo.latitude,
            longitude: photo.Longitude || photo.longitude,
            formItemId: photo.FormItemId || photo.formItemId,
            type: photo.Type || photo.type
          }));
          this.internalMedia = normalizedMedia;
          this.$emit("media-updated", this.internalMedia);
        } else {
          // Fallback: remove from local arrays if findingId not available
          if (this.media && this.media.length) {
            const index = this.media.findIndex(
              (p) => (p.photoUrlId || p.PhotoUrlId) === photoUrlId
            );
            if (index !== -1) {
              this.media.splice(index, 1);
            }
          }
          if (this.internalMedia && this.internalMedia.length) {
            const index = this.internalMedia.findIndex(
              (p) => (p.photoUrlId || p.PhotoUrlId) === photoUrlId
            );
            if (index !== -1) {
              this.internalMedia.splice(index, 1);
            }
          }
        }
      } catch (err) {
        console.error("Error deleting photo:", err);
        console.error("Photo URL ID attempted:", photoUrlId);
        console.error("Error response:", err?.response);
        console.error("Error response data:", err?.response?.data);
        const errorMsg = err?.response?.data?.error || err?.response?.data?.message || err?.response?.data?.details || err?.message || "Failed to delete image";
        const fullErrorMsg = err?.response?.data?.innerException
          ? `${errorMsg} (${err.response.data.innerException})`
          : errorMsg;
        this.toast.error(fullErrorMsg);
      } finally {
        this.deletingPhoto = false;
      }
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    },
    async onPhotoInputChange(e) {
      // iOS-safe: read picked files into memory-backed Blobs immediately so
      // iOS can't evict the camera-temp file before the user clicks Upload.
      // Append (don't overwrite) so successive Add Photos clicks accumulate.
      const files = Array.from(e.target?.files || []);
      if (e.target) e.target.value = "";
      if (!files.length) return;
      const snaps = await snapshotPhotoFiles(files);
      if (snaps.length) {
        this.photoSnapshots = [...(this.photoSnapshots || []), ...snaps];
      }
      if (snaps.length < files.length) {
        this.toast?.error?.(`${files.length - snaps.length} photo(s) could not be read from the camera. Try again.`);
      }
    },
    removeQueuedPhoto(idx) {
      const next = Array.isArray(this.photoSnapshots) ? [...this.photoSnapshots] : [];
      const [removed] = next.splice(idx, 1);
      revokePhotoSnapshot(removed);
      this.photoSnapshots = next;
    },

    async uploadSelectedPhotos() {
      const snaps = this.photoSnapshots || [];
      if (!snaps.length) {
        this.toast.error("No images selected");
        return;
      }

      console.log(`[UPLOAD] Files to upload:`, snaps.map(s => `${s.name} (${s.size} bytes)`));

      if (!this.finding) {
        await this.dialog.confirm({
          title: "Finding Not Loaded",
          message: "Please wait for the finding to load before uploading images.",
          variant: "warn",
          buttons: "ok"
        });
        revokePhotoSnapshots(this.photoSnapshots);
        this.photoSnapshots = [];
        return;
      }

      if (!this.finding.findingId) {
        await this.dialog.confirm({
          title: "Cannot Upload Image",
          message: "This finding has not been saved yet. Please save the finding first before uploading images.",
          variant: "warn",
          buttons: "ok"
        });
        revokePhotoSnapshots(this.photoSnapshots);
        this.photoSnapshots = [];
        return;
      }

      const findingId = this.finding.findingId;
      this.uploadingPhoto = true;
      const uploadPromises = [];

      for (const snap of snaps) {
        const photoUrlId = crypto.randomUUID();
        console.log(`[UPLOAD] Starting upload for file: ${snap.name} (${snap.size} bytes, type: ${snap.type})`);
        console.log(`[UPLOAD] PhotoUrlId: ${photoUrlId}, FindingId: ${findingId}`);

        const photoItem = {
          Blob: snap.blob,
          PhotoUrlId: photoUrlId,
          FindingId: findingId,
          CreatedAt: new Date().toISOString(),
        };

        uploadPromises.push(
          uploadPhoto(photoItem)
            .then(response => {
              console.log(`[UPLOAD] Success for ${snap.name}:`, response?.data);
              return response;
            })
            .catch(error => {
              console.error(`[UPLOAD] Failed for ${snap.name}:`, error);
              console.error(`[UPLOAD] Error response:`, error?.response?.data);
              throw error;
            })
        );
      }

      try {
        console.log(`[UPLOAD] Starting upload of ${snaps.length} file(s)`);
        const results = await Promise.all(uploadPromises);
        console.log(`[UPLOAD] All uploads completed. Results:`, results);
        this.toast.success(`${snaps.length} image${snaps.length !== 1 ? 's' : ''} uploaded successfully`);

        // Release the in-memory snapshots — bytes are now on the server.
        revokePhotoSnapshots(this.photoSnapshots);
        this.photoSnapshots = [];

        // Refresh media list - wait a moment for backend to process
        if (this.finding?.findingId) {
          // Small delay to ensure backend has saved the photos
          await new Promise(resolve => setTimeout(resolve, 500));

          // Fetch fresh media from API
          console.log(`[UPLOAD] Fetching media for findingId: ${this.finding.findingId}`);
          const freshMedia = await getPhotosByFindingId(this.finding.findingId);
          console.log(`[UPLOAD] Refreshed media after upload (raw):`, freshMedia);
          console.log(`[UPLOAD] Media count: ${freshMedia?.length || 0}`);

          // Normalize the response to lowercase keys (backend returns PascalCase, frontend expects camelCase)
          const normalizedMedia = (freshMedia || []).map(photo => {
            const normalized = {
              photoUrlId: photo.PhotoUrlId || photo.photoUrlId,
              url: photo.Url || photo.url,
              customerId: photo.CustomerId || photo.customerId,
              zoneId: photo.ZoneId || photo.zoneId,
              findingId: photo.FindingId || photo.findingId,
              localUrl: photo.LocalUrl || photo.localUrl,
              synced: photo.Synced || photo.synced,
              createdAt: photo.CreatedAt || photo.createdAt,
              latitude: photo.Latitude || photo.latitude,
              longitude: photo.Longitude || photo.longitude,
              formItemId: photo.FormItemId || photo.formItemId,
              type: photo.Type || photo.type
            };
            console.log(`[UPLOAD] Normalized photo:`, normalized);
            return normalized;
          });

          console.log(`[UPLOAD] Normalized media array (${normalizedMedia.length} items):`, normalizedMedia);

          // Update both internalMedia and emit to parent
          this.internalMedia = normalizedMedia;
          console.log(`[UPLOAD] Updated internalMedia. Length: ${this.internalMedia.length}`);
          console.log(`[UPLOAD] internalMedia contents:`, JSON.stringify(this.internalMedia, null, 2));

          // Force Vue reactivity
          this.$forceUpdate();

          console.log(`[UPLOAD] After forceUpdate - effectiveMedia length: ${this.effectiveMedia.length}`);
          console.log(`[UPLOAD] effectiveMedia contents:`, JSON.stringify(this.effectiveMedia, null, 2));
          console.log(`[UPLOAD] effectiveMedia first item URL:`, this.effectiveMedia[0]?.url || this.effectiveMedia[0]?.Url);

          this.$emit("media-updated", this.internalMedia);
        }
      } catch (err) {
        console.error("Error uploading photos:", err);
        console.error("Error response:", err?.response);
        console.error("Error details:", err?.response?.data);
        console.error("Validation errors object:", err?.response?.data?.errors);
        console.error("Full error data:", JSON.stringify(err?.response?.data, null, 2));

        // Show validation errors if present
        const validationErrors = err?.response?.data?.errors;
        let errorMsg = err?.response?.data?.error || err?.response?.data?.message || err?.response?.data?.details || err?.message || "Failed to upload image(s)";

        if (validationErrors) {
          const errorList = Object.entries(validationErrors)
            .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
            .join('\n');
          errorMsg = `Validation errors:\n${errorList}`;
          console.error("Parsed validation errors:", errorList);
        }

        const fullErrorMsg = err?.response?.data?.innerException
          ? `${errorMsg} (${err.response.data.innerException})`
          : errorMsg;
        await this.dialog.confirm({
          title: "Upload Failed",
          message: fullErrorMsg,
          variant: "error",
          buttons: "ok"
        });
      } finally {
        this.uploadingPhoto = false;
        // Release any remaining snapshots (e.g. error path where we kept them
        // so the user could retry). If they're already empty this is a no-op.
        revokePhotoSnapshots(this.photoSnapshots);
        this.photoSnapshots = [];
      }
    },
  },
  beforeUnmount() {
    this.destroyMap();
    revokePhotoSnapshots(this.photoSnapshots);
  },
};
</script>

<style scoped>
/* … keep your styles … */
.fs-card {
  position: relative;
  border-radius: 20px;
  border: 1px solid #e7edf3;
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.06);
  background: linear-gradient(180deg, #ffffff 0%, #fbfcfd 100%);
}

.fs-card :deep(.v-card-title) {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 18px;
}

.fs-title2 {
  font-size: 1.35rem;
  font-weight: 800;
  color: #1f2937;
  letter-spacing: -0.02em;
}

.fs-title {
  font-size: 1.65rem;
  line-height: 1.1;
  font-weight: 800;
  color: #1f2937;
}

.fs-chip {
  font-weight: 700;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
}

.fs-section-heading {
  font-size: 0.82rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #667085;
}

.fs-info-row {
  padding: 0.55rem 0.75rem;
  border: 1px solid #e7edf3;
  border-radius: 12px;
  background: #fff;
}

.fs-user-avatar {
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
}

.fs-meta-card {
  padding: 0.6rem 0.75rem;
  border: 1px solid #e7edf3;
  border-radius: 12px;
  background: #fff;
}

.fs-info-value {
  color: #344054;
}

.fs-form-row {
  display: flex;
  gap: 12px;
  padding: 0.4rem 0;
  border-bottom: 1px solid #f0f3f7;
}

.fs-form-row:last-child {
  border-bottom: none;
}

.fs-form-label {
  flex: 0 0 40%;
  font-weight: 600;
  color: #667085;
  font-size: 0.85rem;
}

.fs-form-value {
  flex: 1 1 60%;
  color: #344054;
  font-size: 0.9rem;
  word-break: break-word;
}

.fs-form-signature {
  max-width: 160px;
  max-height: 80px;
  object-fit: contain;
  border: 1px solid #e7edf3;
  border-radius: 8px;
  background: #fff;
}

.fs-user-name {
  display: inline-block;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.fs-description {
  color: #344054;
  line-height: 1.45;
}

.fs-meta-select {
  min-width: 180px;
}

.fs-card :deep(.fs-meta-select .v-field) {
  background: #fff;
  border-radius: 12px !important;
}

.fs-card :deep(.fs-meta-select .v-field--variant-filled),
.fs-card :deep(.fs-meta-select .v-field--variant-solo),
.fs-card :deep(.fs-meta-select .v-field--variant-solo-filled) {
  box-shadow: none !important;
}

.fs-card :deep(.fs-meta-select .v-field__outline) {
  color: #d0d5dd !important;
}

.fs-card :deep(.fs-meta-select .v-field--focused .v-field__outline) {
  color: #137752 !important;
}
.fs-close {
  position: absolute;
  top: 6px;
  right: 6px;
  z-index: 3;
}
.fs-edit {
  position: absolute;
  top: 17px;
  right: 50px;
  z-index: 3;
}

.fs-card :deep(.leaflet-container) {
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid #e7edf3;
}

.fs-card :deep(.coord-block) {
  padding: 0.6rem 0.75rem;
  border: 1px solid #e7edf3;
  border-radius: 12px;
  background: #fff;
}
</style>

<style>
/* Unscoped so it targets the teleported dialog overlay */
.lightbox-dialog {
  background: rgba(0, 0, 0, 0.85) !important;
  box-shadow: none !important;
  overflow: hidden !important;
}

.lightbox-overlay {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100vw;
  height: 100vh;
  position: relative;
}

.lightbox-image {
  max-width: 92vw;
  max-height: 92vh;
  object-fit: contain;
  border-radius: 6px;
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.6);
}

.lightbox-close {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.lightbox-analyse {
  position: absolute;
  left: 50%;
  bottom: 24px;
  transform: translateX(-50%);
  z-index: 10;
}

.lightbox-close:hover {
  transform: scale(1.1);
}
</style>
